import React, { useCallback, useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { Select, MenuItem, Typography, Paper, Divider, IconButton, Tooltip, FormControl, InputLabel, Box, Button, CircularProgress, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadIcon from '@mui/icons-material/Upload';
import ReceiptIcon from '@mui/icons-material/Receipt';
import moment from 'moment';
import constants from '../../utilities/constants';
import debounce from 'lodash/debounce';
import { StateManager } from '../../utilities/stateManager';
import firebase from '../../utilities/firebase';
import { useStackZIndex } from '../../utilities/ModalStackContext';
import { serviceLineConfirmText } from '../../utilities/partServiceLine';

const PartEditForm = ({ part, onUpdate, onCancel, onSubmit, onDelete }) => {
  const popperZ = useStackZIndex();
  const costInputRef = useRef(null);
  const [localStatus, setLocalStatus] = useState(part.status || 'pending');
  const [localOrder, setLocalOrder] = useState(part.order || '');
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  // Sync local state when a different part is opened
  useEffect(() => {
    setLocalStatus(part.status || 'pending');
    setLocalOrder(part.order || '');
  }, [part.id]);

  // 'declined' is a terminal status set only via the Decline button (below),
  // never chosen by hand — keep it out of the manual status dropdown.
  const statusOptions = constants.part_statuses
    .filter(status => status !== 'declined')
    .map(status => ({
      value: status,
      label: status.charAt(0).toUpperCase() + status.slice(1)
    }));

  // The linked purchase is kept in sync by the syncPartPurchase Cloud Function
  // trigger that fires on every part write — this form just writes the part.
  const debouncedUpdate = useCallback(
    debounce((updates) => {
      onUpdate(updates);
    }, 300),
    [onUpdate]
  );

  // Handle text field changes with debouncing
  const handleTextChange = (field) => (e) => {
    const value = e.target.value;
    // Update the local state immediately for responsive UI
    e.target.value = value;
    if (field === 'order') setLocalOrder(value);
    // Debounce the actual update
    debouncedUpdate({ [field]: value });
  };

  // Handle number field changes with debouncing
  const handleNumberChange = (field) => (e) => {
    const inputValue = e.target.value;
    // Allow empty string or valid numbers (including decimals)
    if (inputValue === '' || /^\d*\.?\d*$/.test(inputValue)) {
      // Only parse to number for the update, don't modify the input value
      const numericValue = inputValue === '' ? 0 : parseFloat(inputValue) || 0;
      // Debounce the actual update
      debouncedUpdate({ [field]: numericValue });
    }
  };

  // Handle return receipt upload
  const handleReturnReceiptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const storageRef = firebase.storage().ref(`parts/${part.id}/return-receipt`);
      await storageRef.put(file);
      const url = await storageRef.getDownloadURL();
      onUpdate({ returnReceipt: url });
    } catch (error) {
      console.error('Error uploading receipt:', error);
      StateManager.setAlertAndOpen("Error uploading return receipt", "error");
    } finally {
      setUploadingReceipt(false);
    }
  };

  // Handle select changes immediately (no debounce needed)
  const handleSelectChange = (field) => (e) => {
    const value = e.target.value;
    const updates = { [field]: value };

    if (field === 'status') {
      // Block 'returned' if no return receipt has been uploaded
      if (value === 'returned' && !part.returnReceipt) {
        StateManager.setAlertAndOpen("Upload a return receipt before marking as returned", "error");
        return;
      }
      // The SO is still an unapproved estimate — confirm before moving the part
      // forward (ordering against an estimate risks buying unsold work). Only
      // forward moves prompt; corrections back down the pipeline pass through.
      if (part.order_status === 'estimate') {
        const flow = constants.part_statuses;
        const advancing = flow.indexOf(value) > flow.indexOf(part.status);
        if (advancing && !window.confirm(
          `${part.order || 'This service order'} is still an estimate — the work hasn't been approved yet.\n\nMove "${part.name || 'this part'}" to ${value} anyway?`
        )) return;
      }
      // Approving a part not tied to an open service line commits to work that
      // may not happen.
      if (part.status === 'pending' && value === 'approved') {
        const lineWarning = serviceLineConfirmText([part]);
        if (lineWarning && !window.confirm(lineWarning)) return;
      }
      updates.status_time = moment().format('YYYY/MM/DD');
      setLocalStatus(value);
      onUpdate(updates);
      return;
    }
    onUpdate(updates);
  };

  // Handle copy ID to clipboard
  const handleCopyId = () => {
    navigator.clipboard.writeText(part.id);
  };

  // Open the service order in the global SO modal, layered above this dialog
  // (allocateZ gives the modal a fresh top z-index on open) — not a new tab.
  const handleOpenServiceOrder = () => {
    if (localOrder) {
      StateManager.openServiceOrderModal(localOrder);
    }
  };

  // Handle date changes
  const handleDateChange = (field) => (date) => {
    const formattedDate = date ? moment(date).format('YYYY/MM/DD') : null;
    onUpdate({ [field]: formattedDate });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 0 }}>
                    Part Details
                  </Typography>
                  {part.declined && (
                    <Chip label="Declined" color="error" size="small" sx={{ fontWeight: 600 }} />
                  )}
                </Box>
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    size="small"
                    variant={part.declined ? 'contained' : 'outlined'}
                    color={part.declined ? 'success' : 'error'}
                    onClick={() => {
                      if (part.declined) {
                        // Reinstate: restore the status the part held before it
                        // was declined (default to pending if unknown).
                        onUpdate({
                          declined: false,
                          status: part.restoreStatus || 'pending',
                          restoreStatus: null,
                          status_time: moment().format('YYYY/MM/DD'),
                        });
                        StateManager.setAlertAndOpen('Part reinstated', 'success');
                      } else {
                        // Decline: move to the terminal 'declined' status so it
                        // drops out of the parts pipeline, remembering the prior
                        // status so Reinstate can put it back. Keep the boolean
                        // so accounting/estimate logic keeps treating it as
                        // declined.
                        onUpdate({
                          declined: true,
                          status: 'declined',
                          restoreStatus: part.status || 'pending',
                          status_time: moment().format('YYYY/MM/DD'),
                        });
                        StateManager.setAlertAndOpen('Part declined — excluded from accounting', 'warning');
                      }
                    }}
                  >
                    {part.declined ? 'Reinstate' : 'Decline'}
                  </Button>
                  <Tooltip title="Copy Part ID">
                    <IconButton onClick={handleCopyId} size="small">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Open Service Order">
                    <IconButton
                      onClick={handleOpenServiceOrder}
                      size="small"
                      disabled={!localOrder}
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton onClick={onCancel} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          {/* Image and Vehicle Info Section */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    height: 200, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'action.hover',
                    borderRadius: 1
                  }}
                >
                  {part.car_thumbnail ? (
                    <img 
                      src={part.car_thumbnail} 
                      alt="Vehicle" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '100%', 
                        objectFit: 'contain' 
                      }} 
                    />
                  ) : (
                    <DirectionsCarIcon sx={{ fontSize: 80, color: 'action.disabled' }} />
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Service Order Number"
                      defaultValue={part.order || ''}
                      onChange={handleTextChange('order')}
                      InputProps={{
                        readOnly: !StateManager.isUserAllowed('parts-pipeline'),
                        sx: {
                          '& .MuiInputBase-input': {
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Vehicle Information"
                      value={`${part.car_title}`}
                      InputProps={{
                        readOnly: true,
                        sx: { 
                          '& .MuiInputBase-input': { 
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Basic Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Basic Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Part Name"
                  defaultValue={part.name || ''}
                  onChange={handleTextChange('name')}
                  InputProps={{ 
                    style: { fontSize: 18, fontWeight: 'bold' },
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Part Number"
                  defaultValue={part.partNumber || ''}
                  onChange={handleTextChange('partNumber')}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Quantity"
                  defaultValue={part.quantity || 1}
                  onChange={handleNumberChange('quantity')}
                  inputProps={{
                    min: "1",
                    step: "1"
                  }}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vendor"
                  defaultValue={part.vendor || ''}
                  onChange={handleTextChange('vendor')}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vendor Link"
                  defaultValue={part.link || ''}
                  onChange={handleTextChange('link')}
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  defaultValue={part.location || ''}
                  onChange={handleTextChange('location')}
                  autoComplete="off"
                  InputProps={{
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={localStatus}
                    onChange={handleSelectChange('status')}
                    sx={{
                      '& .MuiSelect-select': { py: 1.5 },
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    {statusOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          {/* Dates Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Dates
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Order Date"
                  value={part.orderDate ? moment(part.orderDate) : null}
                  onChange={handleDateChange('orderDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    },
                    popper: { sx: { zIndex: popperZ } },
                    desktopPaper: { sx: { zIndex: popperZ } },
                    mobilePaper: { sx: { zIndex: popperZ } },
                    dialog: { sx: { zIndex: popperZ } },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Est. Arrival Date"
                  value={part.arrivalDate ? moment(part.arrivalDate) : null}
                  onChange={handleDateChange('arrivalDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    },
                    popper: { sx: { zIndex: popperZ } },
                    desktopPaper: { sx: { zIndex: popperZ } },
                    mobilePaper: { sx: { zIndex: popperZ } },
                    dialog: { sx: { zIndex: popperZ } },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Return Date"
                  value={part.returnDate ? moment(part.returnDate) : null}
                  onChange={handleDateChange('returnDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    },
                    popper: { sx: { zIndex: popperZ } },
                    desktopPaper: { sx: { zIndex: popperZ } },
                    mobilePaper: { sx: { zIndex: popperZ } },
                    dialog: { sx: { zIndex: popperZ } },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Financial Information Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Financial Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Unit Cost"
                  defaultValue={part.cost || 0}
                  onChange={handleNumberChange('cost')}
                  inputRef={costInputRef}
                  inputProps={{
                    step: "0.01",
                    min: "0"
                  }}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                    sx: { '&:hover': { backgroundColor: 'action.hover' } }
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Return Information (only when returning or returned) */}
          {(localStatus === 'returning' || localStatus === 'returned') && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                Return Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Return Amount"
                    defaultValue={part.returnAmount || 0}
                    onChange={handleNumberChange('returnAmount')}
                    inputProps={{ step: '0.01', min: '0' }}
                    helperText="Amount received back from vendor"
                    InputProps={{
                      startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                      sx: { '&:hover': { backgroundColor: 'action.hover' } }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {part.returnReceipt ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                        Return receipt uploaded
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ReceiptIcon />}
                          onClick={() => window.open(part.returnReceipt, '_blank')}
                        >
                          View Receipt
                        </Button>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          startIcon={uploadingReceipt ? <CircularProgress size={16} /> : <UploadIcon />}
                          disabled={uploadingReceipt}
                        >
                          Replace
                          <input type="file" hidden accept="image/*,application/pdf" onChange={handleReturnReceiptUpload} />
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Return receipt required to mark as returned
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={uploadingReceipt ? <CircularProgress size={16} /> : <UploadIcon />}
                        disabled={uploadingReceipt}
                      >
                        {uploadingReceipt ? 'Uploading...' : 'Upload Return Receipt'}
                        <input type="file" hidden accept="image/*,application/pdf" onChange={handleReturnReceiptUpload} />
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Notes Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
              Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notes"
              defaultValue={part.notes || ''}
              onChange={handleTextChange('notes')}
              InputProps={{
                sx: { '&:hover': { backgroundColor: 'action.hover' } }
              }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 2 }}>
              <Button 
                variant="outlined" 
                color="error"
                onClick={onDelete}
                sx={{ minWidth: 100 }}
              >
                Delete Part
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={onCancel}
                  sx={{ minWidth: 100 }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  onClick={onSubmit}
                  sx={{ minWidth: 100 }}
                >
                  Save Changes
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </LocalizationProvider>
  );
};

export default PartEditForm; 